import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";
import { useFileDownload } from "../../ui/useFileDownload";

type ListStatisticsResponse =
  paths["/statistics"]["get"]["responses"]["200"]["content"]["application/json"];

export type StatisticListItem = ListStatisticsResponse["items"][number];

export interface StatisticListFilters {
  search: string;
  language: string;
  region: string;
  domains: string[];
}

export const EMPTY_FILTERS: StatisticListFilters = {
  search: "",
  language: "",
  region: "",
  domains: [],
};

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: StatisticListItem[]; totalCount: number }
  | { kind: "error"; message: string };

type ExportVariant = paths["/statistics/export"]["get"]["parameters"]["query"]["variant"];

type ExportState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

const DEFAULT_PAGE_SIZE = 25;

export interface UseStatisticListResult {
  state: ScreenState;
  fetching: boolean;
  page: number;
  pageSize: number;
  exportState: ExportState;
  setFilters: (filters: StatisticListFilters) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refetch: () => void;
  exportCsv: (variant: ExportVariant) => void;
}

export function useStatisticList(): UseStatisticListResult {
  const downloadFile = useFileDownload();
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [filters, setFiltersState] = useState<StatisticListFilters>(EMPTY_FILTERS);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [exportState, setExportState] = useState<ExportState>({ kind: "idle" });
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const fetchStatistics = useCallback(
    async (currentFilters: StatisticListFilters, currentPage: number, currentPageSize: number) => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (!initialLoadDone.current) {
        setState({ kind: "loading" });
      }

      setFetching(true);

      const parameters = new URLSearchParams();
      parameters.set("page", String(currentPage));
      parameters.set("pageSize", String(currentPageSize));
      if (currentFilters.search) {
        parameters.set("search", currentFilters.search);
      }
      if (currentFilters.language) {
        parameters.set("language", currentFilters.language);
      }
      if (currentFilters.region) {
        parameters.set("region", currentFilters.region);
      }
      currentFilters.domains.forEach((domain) => {
        parameters.append("domains", domain);
      });

      try {
        const response = await fetchWithRefresh(`/api/statistics?${parameters.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as ListStatisticsResponse;

        initialLoadDone.current = true;

        if (data.items.length === 0 && data.totalCount === 0) {
          setState({ kind: "empty" });
        } else {
          setState({
            kind: "data",
            items: data.items,
            totalCount: data.totalCount,
          });
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch statistics");
        setState({ kind: "error", message });
      } finally {
        if (!controller.signal.aborted) {
          setFetching(false);
        }
      }
    },
    [],
  );

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const pageRef = useRef(page);
  pageRef.current = page;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  useEffect(() => {
    void fetchStatistics(filters, page, pageSize);
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [filters, page, pageSize, fetchStatistics]);

  const refetch = useCallback(() => {
    void fetchStatistics(filtersRef.current, pageRef.current, pageSizeRef.current);
  }, [fetchStatistics]);

  const setFilters = useCallback((newFilters: StatisticListFilters) => {
    setFiltersState(newFilters);
    setPageState(1);
  }, []);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1);
  }, []);

  const exportCsv = useCallback(
    (variant: ExportVariant) => {
      setExportState({ kind: "loading" });

      const parameters = new URLSearchParams();
      parameters.set("variant", variant);
      if (filtersRef.current.search) {
        parameters.set("search", filtersRef.current.search);
      }
      if (filtersRef.current.language) {
        parameters.set("language", filtersRef.current.language);
      }
      if (filtersRef.current.region) {
        parameters.set("region", filtersRef.current.region);
      }
      filtersRef.current.domains.forEach((domain) => {
        parameters.append("domains", domain);
      });

      fetchWithRefresh(`/api/statistics/export?${parameters.toString()}`)
        .then(async (response) => {
          if (!response.ok) {
            const body = (await response.json()) as { error: string };
            throw new Error(body.error);
          }
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          const fileNameMatch = contentDisposition
            ? contentDisposition.match(/filename="(.+)"/)
            : null;
          const fileName = fileNameMatch ? fileNameMatch[1] : "statistics.csv";

          downloadFile(blob, fileName);

          setExportState({ kind: "idle" });
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : "Export failed";
          logger.error({ err: message }, "Failed to export statistics");
          setExportState({ kind: "error", message });
        });
    },
    [],
  );

  return {
    state,
    fetching,
    page,
    pageSize,
    exportState,
    setFilters,
    setPage,
    setPageSize,
    refetch,
    exportCsv,
  };
}
